import { create } from "zustand";

/**
 * Spec 030 — Estado de la sesión de soporte (impersonación desde HQ).
 *
 * `enter` toma un snapshot de la sesión del super-admin, cambia el access_token
 * por el de soporte y recarga la app en el contexto del negocio. `exit` restaura
 * la sesión original y vuelve a HQ. El estado se persiste en localStorage
 * (claves namespaced hq_imp_*) para sobrevivir a la recarga; `hydrate` lo lee al
 * arrancar la app. Ver research.md D7/D8 y data-model.md §4.
 */

const K_ACTIVE = "hq_imp_active";
const K_BUSINESS = "hq_imp_business";
const K_ORIGIN = "hq_imp_origin";

interface OriginSnapshot {
  access_token: string | null;
  account_info: string | null;
  login_warnings: string | null;
}

interface ImpersonationState {
  active: boolean;
  businessName: string | null;
  hydrate: () => void;
  enter: (token: string, businessName: string) => void;
  exit: () => void;
}

export const useImpersonationStore = create<ImpersonationState>((set) => ({
  active: false,
  businessName: null,

  hydrate: () => {
    const active = localStorage.getItem(K_ACTIVE) === "1";
    set({
      active,
      businessName: active ? localStorage.getItem(K_BUSINESS) : null,
    });
  },

  enter: (token: string, businessName: string) => {
    // 1) Snapshot de la sesión actual (super-admin).
    const origin: OriginSnapshot = {
      access_token: localStorage.getItem("access_token"),
      account_info: localStorage.getItem("account_info"),
      login_warnings: localStorage.getItem("login_warnings"),
    };
    localStorage.setItem(K_ORIGIN, JSON.stringify(origin));
    localStorage.setItem(K_ACTIVE, "1");
    localStorage.setItem(K_BUSINESS, businessName);

    // 2) Cambiar al token de soporte y limpiar restos de la sesión anterior que
    //    no aplican al negocio impersonado (account_info del super-admin, warnings).
    localStorage.setItem("access_token", token);
    localStorage.removeItem("account_info");
    localStorage.removeItem("login_warnings");

    // 3) Recarga real montando la app en el nuevo contexto.
    window.location.assign("/estadisticas");
  },

  exit: () => {
    const raw = localStorage.getItem(K_ORIGIN);
    if (!raw) {
      // Estado corrupto: sin snapshot no podemos restaurar → sesión limpia.
      localStorage.clear();
      window.location.assign("/");
      return;
    }
    let origin: OriginSnapshot;
    try {
      origin = JSON.parse(raw);
    } catch {
      localStorage.clear();
      window.location.assign("/");
      return;
    }

    // Restaurar la sesión del super-admin.
    if (origin.access_token) localStorage.setItem("access_token", origin.access_token);
    else localStorage.removeItem("access_token");
    if (origin.account_info) localStorage.setItem("account_info", origin.account_info);
    else localStorage.removeItem("account_info");
    if (origin.login_warnings) localStorage.setItem("login_warnings", origin.login_warnings);
    else localStorage.removeItem("login_warnings");

    // Limpiar el estado de impersonación.
    localStorage.removeItem(K_ACTIVE);
    localStorage.removeItem(K_BUSINESS);
    localStorage.removeItem(K_ORIGIN);

    window.location.assign("/hq/clientes");
  },
}));
